import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Minus, Plus } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { useLanguage } from '../../i18n/language.js';
import { newIdempotencyKey } from '../../workspace/api.js';
import { InlineAlert, Spinner } from '../../workspace/ui.js';
import {
  createProductionLine,
  fetchMerchantIntake,
  fetchProductionLineSetup,
  MERCHANT_INTAKE_STORAGE_KEY,
  type MerchantProfileView,
  type ProductionLinePurpose,
  type ProductionLineSetupView,
} from '../../merchant/api.js';

interface SetupData {
  readonly intake: MerchantProfileView;
  readonly setup: ProductionLineSetupView;
}

const COPY = {
  en: {
    title: 'Create a Production Line',
    intro:
      'Choose what this Production Line should accomplish and how many videos it should produce each day. Mouja will reuse your approved business and market context.',
    confirmed: 'Using your confirmed profile',
    confirmedNote: 'Read-only. Nothing here is asked again.',
    purposeLegend: 'What is this Production Line for?',
    purposeHelp:
      'Mouja uses this to decide what your videos should do for you. You can change it later.',
    required: 'Required',
    otherLabel: 'In your own words',
    otherPlaceholder: 'Bring people back after Ramadan',
    otherHelp: 'One line is enough. Mouja works out the rest.',
    cadence: 'Videos per day',
    cadenceHint: 'Your plan allows between',
    cadenceJoin: 'and',
    cadenceSuffix: 'per day.',
    video: 'video',
    videos: 'videos',
    save: 'Save and continue',
    saving: 'Saving your Production Line…',
    missing: 'No confirmed business profile was found in this browser.',
    returnHome: 'Return to Mouja',
    unavailableTitle: 'Production Line setup is unavailable',
    unavailableBody:
      'Complete and confirm the Business Profile and competitor foundation first, then try again.',
    requiredOther: 'Add a short description for this purpose.',
    selectPurpose: 'Choose one purpose to continue.',
    skip: 'Skip to content',
  },
  ar: {
    title: 'سوِّ خط إنتاج',
    intro:
      'اختر وش تبي خط الإنتاج هذا يحقق، وكم فيديو ينتج كل يوم. موجة بتستخدم سياق نشاطك وسوقك المعتمد من قبل.',
    confirmed: 'بالاعتماد على ملفك المعتمد',
    confirmedNote: 'للقراءة بس. وما راح نسألك عنها مرة ثانية.',
    purposeLegend: 'وش الهدف من خط الإنتاج هذا؟',
    purposeHelp: 'موجة تستخدم هذا عشان تحدد وش تسوي لك فيديوهاتك. وتقدر تغيّره بعدين.',
    required: 'مطلوب',
    otherLabel: 'بكلماتك أنت',
    otherPlaceholder: 'أرجّع العملاء بعد رمضان',
    otherHelp: 'سطر واحد يكفي. وموجة تتكفّل بالباقي.',
    cadence: 'عدد الفيديوهات باليوم',
    cadenceHint: 'خطتك تسمح بين',
    cadenceJoin: 'و',
    cadenceSuffix: 'باليوم.',
    video: 'فيديو',
    videos: 'فيديوهات',
    save: 'احفظ وكمّل',
    saving: 'نحفظ خط الإنتاج…',
    missing: 'ما لقينا ملف نشاط معتمد في هذا المتصفح.',
    returnHome: 'رجوع لموجة',
    unavailableTitle: 'إعداد خط الإنتاج مو متاح',
    unavailableBody:
      'كمّل ملف النشاط وتحليل المنافسين واعتمدهم أول، وبعدين جرّب مرة ثانية.',
    requiredOther: 'ضيف وصف مختصر لهذا الهدف.',
    selectPurpose: 'اختر هدف واحد عشان تكمل.',
    skip: 'تخطّى للمحتوى',
  },
} as const;

function rememberedIntake(): string | null {
  try {
    return localStorage.getItem(MERCHANT_INTAKE_STORAGE_KEY);
  } catch {
    return null;
  }
}

function displayValue(value: string | null | undefined, fallback: string): string {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : fallback;
}

export function ProductionLineSetupPage(): ReactNode {
  const { language } = useLanguage();
  const copy = COPY[language];
  const navigate = useNavigate();
  const { intakeId: routeIntakeId } = useParams<{ intakeId: string }>();
  const rememberedIntakeId = useMemo(rememberedIntake, []);
  const intakeId = routeIntakeId ?? rememberedIntakeId;
  const [data, setData] = useState<SetupData | null>(null);
  const [loading, setLoading] = useState(intakeId !== null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [purpose, setPurpose] = useState<ProductionLinePurpose | null>(null);
  const [otherPurpose, setOtherPurpose] = useState('');
  const [videosPerDay, setVideosPerDay] = useState<number | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const intentKey = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (intakeId === null) return;
    setLoading(true);
    setLoadFailed(false);
    try {
      const [intake, setup] = await Promise.all([
        fetchMerchantIntake(intakeId),
        fetchProductionLineSetup(intakeId),
      ]);
      setData({ intake, setup });
      setVideosPerDay(setup.entitlement.current);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [intakeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const changePurpose = (next: ProductionLinePurpose) => {
    setPurpose(next);
    if (next !== 'OTHER') setOtherPurpose('');
    setValidationError(null);
    setSubmitError(null);
    intentKey.current = null;
  };

  const adjustCadence = (delta: number) => {
    if (data === null || videosPerDay === null) return;
    const next = Math.min(
      data.setup.entitlement.max,
      Math.max(data.setup.entitlement.min, videosPerDay + delta),
    );
    setVideosPerDay(next);
    setSubmitError(null);
    intentKey.current = null;
  };

  const submit = async () => {
    if (intakeId === null || data === null || videosPerDay === null) return;
    if (purpose === null) {
      setValidationError(copy.selectPurpose);
      return;
    }
    const note = otherPurpose.trim();
    if (purpose === 'OTHER' && note.length === 0) {
      setValidationError(copy.requiredOther);
      return;
    }
    setValidationError(null);
    setSubmitError(null);
    setSaving(true);
    intentKey.current ??= newIdempotencyKey();
    try {
      const result = await createProductionLine(
        intakeId,
        {
          purpose: { type: purpose, note: purpose === 'OTHER' ? note : null },
          videosPerDay,
        },
        intentKey.current,
      );
      intentKey.current = null;
      if (result.continuation.destination !== null) {
        void navigate(result.continuation.destination, { replace: true });
        return;
      }
      void navigate(
        `/production-lines/${encodeURIComponent(result.productionLine.productionLineId)}/taste-calibration`,
        { replace: true },
      );
    } catch {
      setSubmitError(
        language === 'ar'
          ? 'ما قدرنا نحفظ خط الإنتاج. تأكد من اتصالك وجرّب مرة ثانية.'
          : 'The Production Line could not be saved. Check your connection and try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  if (intakeId === null) {
    return (
      <SetupFrame skip={copy.skip}>
        <InlineAlert tone="warning" title={copy.missing}>
          <button
            className="mj-pl-text-link"
            type="button"
            onClick={() => void navigate('/')}
          >
            {copy.returnHome}
          </button>
        </InlineAlert>
      </SetupFrame>
    );
  }

  if (loading) {
    return (
      <SetupFrame skip={copy.skip}>
        <div className="mj-pl-loading">
          <Spinner
            label={language === 'ar' ? 'جاري تحميل الإعداد…' : 'Loading setup…'}
          />
        </div>
      </SetupFrame>
    );
  }

  if (loadFailed || data === null) {
    return (
      <SetupFrame skip={copy.skip}>
        <InlineAlert tone="warning" title={copy.unavailableTitle}>
          <p>{copy.unavailableBody}</p>
          <button type="button" className="mj-pl-secondary" onClick={() => void load()}>
            {language === 'ar' ? 'جرّب مرة ثانية' : 'Try again'}
          </button>
        </InlineAlert>
      </SetupFrame>
    );
  }

  const profile = data.intake.profile?.payload;
  const businessName = displayValue(
    profile?.identity.value,
    data.intake.business.displayName,
  );
  const businessSummary = [profile?.niche.value, profile?.geography.value]
    .filter((entry): entry is string => Boolean(entry?.trim()))
    .join(' · ');
  const atMinimum = videosPerDay === data.setup.entitlement.min;
  const atMaximum = videosPerDay === data.setup.entitlement.max;

  return (
    <SetupFrame skip={copy.skip}>
      <form
        className="mj-pl-form-panel"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (!saving) void submit();
        }}
      >
        <div className="mj-pl-heading">
          <h1>{copy.title}</h1>
          <p>{copy.intro}</p>
        </div>

        <aside className="mj-pl-profile-summary" aria-label={copy.confirmed}>
          <div>
            <span>{copy.confirmed}</span>
            <strong>{businessName}</strong>
            {businessSummary.length > 0 ? <small>{businessSummary}</small> : null}
            <small>{copy.confirmedNote}</small>
          </div>
        </aside>

        <fieldset className="mj-pl-purpose-group">
          <legend>
            <span>{copy.purposeLegend}</span>
            <small>{copy.required}</small>
          </legend>
          <p className="mj-pl-field-help">{copy.purposeHelp}</p>
          <div className="mj-pl-purpose-grid">
            {data.setup.purposeOptions.map((option) => {
              const disabled = option.state !== 'available';
              return (
                <label
                  key={option.id}
                  className={`mj-pl-purpose${purpose === option.id ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}`}
                >
                  <input
                    type="radio"
                    name="production-purpose"
                    value={option.id}
                    checked={purpose === option.id}
                    disabled={disabled}
                    onChange={() => {
                      changePurpose(option.id);
                    }}
                  />
                  <span>{option.label[language]}</span>
                  {disabled && option.reason !== null ? (
                    <small>{option.reason}</small>
                  ) : null}
                </label>
              );
            })}
          </div>
        </fieldset>

        {purpose === 'OTHER' ? (
          <label className="mj-pl-other">
            <span>{copy.otherLabel}</span>
            <input
              type="text"
              maxLength={data.setup.otherMaxLength}
              value={otherPurpose}
              placeholder={copy.otherPlaceholder}
              onChange={(event) => {
                setOtherPurpose(event.target.value);
                setValidationError(null);
                setSubmitError(null);
                intentKey.current = null;
              }}
            />
            <small>{copy.otherHelp}</small>
          </label>
        ) : null}

        <section className="mj-pl-cadence" aria-labelledby="mj-pl-cadence-title">
          <div>
            <h2 id="mj-pl-cadence-title">
              {copy.cadence} <small>{copy.required}</small>
            </h2>
            <p>
              {copy.cadenceHint} {data.setup.entitlement.min} {copy.cadenceJoin}{' '}
              {data.setup.entitlement.max}{' '}
              {data.setup.entitlement.max === 1 ? copy.video : copy.videos}{' '}
              {copy.cadenceSuffix}
            </p>
          </div>
          <div className="mj-pl-stepper" role="group" aria-label={copy.cadence}>
            <button
              type="button"
              disabled={atMinimum}
              aria-label={language === 'ar' ? 'تقليل العدد' : 'Decrease videos per day'}
              onClick={() => {
                adjustCadence(-1);
              }}
            >
              <Minus size={18} aria-hidden="true" />
            </button>
            <output aria-live="polite">{videosPerDay}</output>
            <button
              type="button"
              disabled={atMaximum}
              aria-label={language === 'ar' ? 'زيادة العدد' : 'Increase videos per day'}
              onClick={() => {
                adjustCadence(1);
              }}
            >
              <Plus size={18} aria-hidden="true" />
            </button>
          </div>
        </section>

        {validationError !== null ? (
          <InlineAlert tone="warning" role="alert">
            {validationError}
          </InlineAlert>
        ) : null}
        {submitError !== null ? (
          <InlineAlert tone="error" role="alert">
            {submitError}
          </InlineAlert>
        ) : null}

        <button type="submit" className="mj-pl-primary" disabled={saving}>
          {saving ? copy.saving : copy.save}
        </button>
      </form>
    </SetupFrame>
  );
}

function SetupFrame({
  children,
  skip,
}: {
  readonly children: ReactNode;
  readonly skip: string;
}): ReactNode {
  return (
    <div className="mj-production-line-page">
      <a className="mj-skip-link" href="#production-line-main">
        {skip}
      </a>
      <div id="production-line-main" className="mj-pl-shell">
        {children}
      </div>
    </div>
  );
}
